import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  GraduationCap, 
  Clock, 
  CheckCircle2,
  BookOpen,
  ArrowRight,
  Loader2
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Learn() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [modulesRes, progressRes] = await Promise.all([
        axios.get(`${API}/learning/modules`),
        axios.get(`${API}/learning/progress`)
      ]);
      setModules(modulesRes.data);
      setProgress(progressRes.data.progress || {});
    } catch (error) {
      console.error('Failed to fetch learning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (moduleId) => {
    setCompleting(true);
    try {
      await axios.post(`${API}/learning/progress/${moduleId}`, null, {
        params: { completed: true }
      });
      setProgress(prev => ({
        ...prev,
        [moduleId]: { completed: true, completed_at: new Date().toISOString() }
      }));
      toast.success(t('Module completed!', 'Moduli imekamilika!'));
    } catch (error) {
      toast.error(t('Failed to update progress', 'Imeshindwa kusasisha maendeleo'));
    } finally {
      setCompleting(false);
    }
  };

  const isCompleted = (moduleId) => progress[moduleId]?.completed;

  const completedCount = Object.values(progress).filter(p => p?.completed).length;
  const totalProgress = modules.length > 0 ? (completedCount / modules.length) * 100 : 0;

  const getDifficultyColor = (difficulty) => {
    const colors = {
      beginner: 'bg-green-100 text-green-700',
      intermediate: 'bg-yellow-100 text-yellow-700',
      advanced: 'bg-red-100 text-red-700'
    };
    return colors[difficulty] || 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (selectedModule) {
    return (
      <Layout>
        <div className="page-enter max-w-4xl mx-auto">
          {/* Module Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedModule(null)}
              className="mb-4"
            >
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              {t('Back to Modules', 'Rudi kwa Moduli')}
            </Button>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                  {language === 'sw' ? selectedModule.title_sw : selectedModule.title_en}
                </h1>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedModule.duration_minutes} {t('mins', 'dakika')}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getDifficultyColor(selectedModule.difficulty || selectedModule.level)}`}>
                    {selectedModule.difficulty || selectedModule.level || 'beginner'}
                  </span>
                </div>
              </div>
              {isCompleted(selectedModule.id) ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {t('Completed', 'Imekamilika')}
                  </span>
                </div>
              ) : (
                <Button
                  onClick={() => handleComplete(selectedModule.id)}
                  disabled={completing}
                  className="rounded-full"
                  data-testid="complete-module-btn"
                >
                  {completing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  {t('Mark Complete', 'Kamilisha')}
                </Button>
              )}
            </div>
          </div>

          {/* Module Content */}
          <Card className="border-border/50">
            <CardContent className="p-6 sm:p-8">
              <div className="prose prose-neutral max-w-none">
                {(() => {
                  const content = language === 'sw' 
                    ? (selectedModule.content_sw || selectedModule.description_sw || '') 
                    : (selectedModule.content_en || selectedModule.description_en || '');
                  
                  if (!content) {
                    return (
                      <div className="text-muted-foreground">
                        <p>{t('Content is being prepared. Please check back soon!', 'Maudhui yanaandaliwa. Tafadhali rudi hivi karibuni!')}</p>
                      </div>
                    );
                  }
                  
                  const formattedContent = content
                    .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mb-4 mt-6 text-foreground">$1</h1>')
                    .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mb-3 mt-5 text-foreground">$1</h2>')
                    .replace(/^### (.*$)/gim, '<h3 class="text-lg font-medium mb-2 mt-4 text-foreground">$1</h3>')
                    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                    .replace(/^- (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
                    .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 mb-1 list-decimal">$1</li>');
                  
                  return (
                    <div 
                      className="text-foreground leading-relaxed whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: formattedContent }}
                    />
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            {t('Learning Modules', 'Moduli za Kujifunza')}
          </h1>
          <p className="text-muted-foreground">
            {t('Step-by-step lessons to master investing', 'Masomo ya hatua kwa hatua kuwa mtaalamu wa uwekezaji')}
          </p>
        </div>

        {/* Progress Card */}
        <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    {t('Your Progress', 'Maendeleo Yako')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {completedCount} / {modules.length} {t('modules completed', 'moduli zimekamilika')}
                  </p>
                </div>
              </div>
              <span className="text-2xl font-bold text-primary">
                {Math.round(totalProgress)}%
              </span>
            </div>
            <Progress value={totalProgress} className="h-2" />
          </CardContent>
        </Card>

        {/* Modules Grid */}
        <div className="grid sm:grid-cols-2 gap-4">
          {modules.map((module, index) => {
            const completed = isCompleted(module.id);
            return (
              <Card 
                key={module.id}
                className={`module-card cursor-pointer stagger-item ${
                  completed ? 'border-green-200 bg-green-50/50' : 'border-border/50'
                }`}
                onClick={() => setSelectedModule(module)}
                data-testid={`module-card-${index}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      completed ? 'bg-green-500' : 'bg-primary/10'
                    }`}>
                      {completed ? (
                        <CheckCircle2 className="w-6 h-6 text-white" />
                      ) : (
                        <BookOpen className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-foreground">
                          {language === 'sw' ? module.title_sw : module.title_en}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs flex-shrink-0 ${getDifficultyColor(module.difficulty)}`}>
                          {module.difficulty}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {language === 'sw' ? module.description_sw : module.description_en}
                      </p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {module.duration_minutes} {t('mins', 'dakika')}
                        </span>
                        {completed && (
                          <span className="text-green-600 font-medium">
                            {t('Completed', 'Imekamilika')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
